import {
  AGGRID_AUTOCOLUMN_ID,
  BLOTTER_SELECTION_MULTI_PARAMS_WITH_CHECKBOXES,
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  cellCsvSafety,
  CollapseAllButton,
  columnTypes,
  ExpandAllButton,
  ExportMenuItems,
  filterByCellValueMenuItem,
  filterByColumnMainMenuItems,
  FormControlSizes,
  getCellDisplayValue,
  getParamsFormatted,
  getRowNodesToOperateOn,
  HStack,
  IconName,
  isGridApiReady,
  MixpanelEvent,
  MixpanelEventSource,
  NotificationVariants,
  selectAll,
  Text,
  Toggle,
  Tooltip,
  useAccordionFilterBuilder,
  useBlotterTable,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useDisclosureWithContext,
  useGlobalToasts,
  useMixpanel,
  usePersistedBlotterTable,
  VStack,
  type AgGridMenuItems,
  type FilterableProperty,
} from '@talos/kyoko';
import type {
  GetContextMenuItemsParams,
  GetMainMenuItemsParams,
  IRowNode,
  ProcessCellForExportParams,
  RowSelectionOptions,
  SelectionChangedEvent,
  ValueFormatterParams,
} from 'ag-grid-community';
import { compact } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { useTradingSettings } from '../../../../providers/TradingSettingsContext';
import { useLPSettlementContext } from '../LPSettlementProvider';
import { useSettleBatchContext } from '../SettleBatchContext';
import type { TradeSettlementColumn, TradeSettlementFilter } from '../types';
import {
  buildBatchDetailsOpenedEventProperties,
  LpSettlementBatchDetailsOpenedFrom,
} from '../utils/lpSettlementMixpanelUtils';
import { getLPSettlementTransactionTypeDisplayLabel } from '../utils/lpSettlementTransactionTypeLabels';
import { AddToBatchDialog } from './AddToBatchDialog';
import type { LPSettlementReportBlotterRow } from './blotter/lpSettlementReportRow';
import { NewManualBatchDialog } from './NewManualBatchDialog';
import { BlotterWrapper } from './styles';
import { canSettleSelectedTrades, useCanSettleSelectedTrades } from './useCanSettleSelectedTrades';
import { useTradeSettlementColumns } from './useTradeSettlementColumns';
import { useUnsettledTransactionsBlotterObs } from './useUnsettledTransactionsBlotterObs';
import { colIDToFilterBuilderKey, useUnsettledTransactionsFilter } from './useUnsettledTransactionsFilter';

interface UnsettledTransactionsBlotterParams {
  blotterID: string;
  defaultColumns: TradeSettlementColumn[];
  portalID: string;
  batchID?: string;
}

export type NewManualBatchDisclosureContext = {
  selectedTrades: LPSettlementReportBlotterRow[];
};

export type AddToBatchDisclosureContext = {
  selectedTrades: LPSettlementReportBlotterRow[];
};

const defaultUnsettledTransactionsFilter: TradeSettlementFilter = {};

function getTransactionTypeLabel(node: IRowNode<LPSettlementReportBlotterRow> | null | undefined): string {
  const rowData = node?.data ?? node?.allLeafChildren?.[0]?.data;
  return getLPSettlementTransactionTypeDisplayLabel(rowData?.TransactionType);
}

function formatAutoGroupColumnValue(params: ValueFormatterParams<LPSettlementReportBlotterRow>): string {
  const groupedColumn = params.node?.group ? params.node.rowGroupColumn : params.node?.parent?.rowGroupColumn;
  if (groupedColumn == null) {
    return String(params.value ?? '');
  }

  if (groupedColumn.getColId() === 'EventID') {
    return getTransactionTypeLabel(params.node);
  }

  const displayValue = getCellDisplayValue({
    ...params,
    column: groupedColumn,
  });
  return String(displayValue ?? '');
}

function formatExportCell(params: ProcessCellForExportParams<LPSettlementReportBlotterRow>): string {
  if (params.column.getColId() !== AGGRID_AUTOCOLUMN_ID) {
    return getParamsFormatted(params);
  }

  const node = params.node;
  const rowGroupColumns = params.api.getRowGroupColumns();
  if (node == null || rowGroupColumns.length === 0) {
    return getParamsFormatted(params);
  }

  return rowGroupColumns
    .map(column => {
      if (column.getColId() === 'EventID') {
        return getTransactionTypeLabel(node);
      }

      return (
        getCellDisplayValue({
          ...params,
          value: params.api.getCellValue({ colKey: column, rowNode: node }),
          column,
        }) ?? 'None'
      );
    })
    .join(' > ');
}

function getTradesForContextMenu(
  params: GetContextMenuItemsParams<LPSettlementReportBlotterRow>
): LPSettlementReportBlotterRow[] {
  const selectedTrades = getRowNodesToOperateOn(params)
    .map(node => node.data)
    .filter((row): row is LPSettlementReportBlotterRow => row != null);
  const clickedNode = params.node;
  if (clickedNode?.isSelected() && selectedTrades.length > 0) {
    return selectedTrades;
  }
  if (clickedNode?.data != null && !clickedNode.group) {
    return [clickedNode.data];
  }
  return [];
}

interface UnsettledTransactionsBlotterContentParams extends UnsettledTransactionsBlotterParams {
  persisted: ReturnType<typeof usePersistedBlotterTable>;
  symbols?: string[];
  filterBuilderAccordion?: ReturnType<typeof useAccordionFilterBuilder>;
  filterableProperties?: FilterableProperty<string>[];
}

const UnsettledTransactionsBlotterContent = ({
  defaultColumns: defaultBlotterColumns,
  portalID,
  batchID,
  persisted,
  symbols,
  filterBuilderAccordion,
  filterableProperties,
}: UnsettledTransactionsBlotterContentParams) => {
  const { showAllDecimalsInBlotters } = useTradingSettings();
  const { columns: defaultColumns, aggFuncs } = useTradeSettlementColumns({ defaultColumns: defaultBlotterColumns });
  const [selectedTrades, setSelectedTrades] = useState<LPSettlementReportBlotterRow[]>([]);
  const manualBatchDialog = useDisclosureWithContext<NewManualBatchDisclosureContext>();
  const addToBatchDialog = useDisclosureWithContext<AddToBatchDisclosureContext>();
  const { openBatchDetails } = useSettleBatchContext();
  const mixpanel = useMixpanel(MixpanelEventSource.PortfolioLpSettlement);
  const { add: addToast } = useGlobalToasts();
  const { showAllDecimals, setShowAllDecimals } = useLPSettlementContext();
  const { canSettle, hasEditLPSettlemenyRole, reason: cannotSettleReason } = useCanSettleSelectedTrades(selectedTrades);

  const readOnly = batchID != null;

  const handleRowSelectionChanged = useCallback(({ api }: SelectionChangedEvent<LPSettlementReportBlotterRow>) => {
    if (!isGridApiReady(api)) {
      return;
    }
    // getSelectedRows() only returns rows that pass isRowSelectable, which already filters out group rows
    const selectedRows = api.getSelectedRows();
    setSelectedTrades(selectedRows);
  }, []);

  const { lpSettlementReportsRowObservable, errorObservable } = useUnsettledTransactionsBlotterObs(batchID, symbols);

  const autoGroupColumnDef = useMemo(
    () => ({
      ...columnTypes.group({
        type: 'group',
        editable: false,
        hide: false,
        sortable: true,
        suppressColumnsToolPanel: false,
        pinned: 'left',
      }),
      valueFormatter: formatAutoGroupColumnValue,
    }),
    []
  );

  const rowSelection = useMemo<RowSelectionOptions>(
    () => ({
      ...BLOTTER_SELECTION_MULTI_PARAMS_WITH_CHECKBOXES,
      checkboxes: !readOnly && hasEditLPSettlemenyRole,
      headerCheckbox: !readOnly && hasEditLPSettlemenyRole,
      hideDisabledCheckboxes: true,
      groupSelects: 'filteredDescendants',
    }),
    [readOnly, hasEditLPSettlemenyRole]
  );

  const blotterTableSettings = useMemo(
    () => ({
      showAllDecimals: showAllDecimals ?? false,
    }),
    [showAllDecimals]
  );

  const getExtraMainMenuItems = useCallbackRef((params: GetMainMenuItemsParams) => {
    if (!filterBuilderAccordion?.openClause) {
      return [];
    }
    return filterByColumnMainMenuItems({
      params,
      colIDToFilterBuilderKey,
      openClause: filterBuilderAccordion.openClause,
      mixpanel,
    });
  });

  const openAddToBatchDialog = useCallback(
    (trades: LPSettlementReportBlotterRow[]) => {
      addToBatchDialog.open({ selectedTrades: trades });
    },
    [addToBatchDialog]
  );

  const openManualBatchDialog = useCallback(
    (trades: LPSettlementReportBlotterRow[]) => {
      manualBatchDialog.open({ selectedTrades: trades });
    },
    [manualBatchDialog]
  );

  const getCustomContextMenuItems = useCallback(
    (
      params: GetContextMenuItemsParams<LPSettlementReportBlotterRow>
    ): AgGridMenuItems<LPSettlementReportBlotterRow> => {
      const items: AgGridMenuItems<LPSettlementReportBlotterRow> = [];
      if (!readOnly && hasEditLPSettlemenyRole) {
        const contextMenuTrades = getTradesForContextMenu(params);
        if (canSettleSelectedTrades(contextMenuTrades).canSettle) {
          items.push({
            name: 'Create Manual Batch',
            icon: `<i class="ag-icon ${IconName.Plus}"/>`,
            action: () => openManualBatchDialog(contextMenuTrades),
          });
          const name =
            contextMenuTrades.length === 1 ? 'Add to Batch' : `Add ${contextMenuTrades.length} Trades to Batch`;
          items.push({
            name,
            icon: `<i class="ag-icon ${IconName.Plus}"/>`,
            action: () => openAddToBatchDialog(contextMenuTrades),
          });
        }
        items.push(selectAll(params, mixpanel));
      }
      if (filterBuilderAccordion?.openClause && filterableProperties) {
        items.push(
          ...filterByCellValueMenuItem({
            params,
            openClause: filterBuilderAccordion.openClause,
            filterableProperties,
            colIDToFilterBuilderKey,
            mixpanel,
          })
        );
      }
      return compact(items);
    },
    [
      filterBuilderAccordion?.openClause,
      filterableProperties,
      mixpanel,
      openAddToBatchDialog,
      openManualBatchDialog,
      readOnly,
      hasEditLPSettlemenyRole,
    ]
  );

  const contextMenuOptions = useMemo(
    () => ({
      getCustomContextMenuItems,
      dataDisplayCallback: (row: LPSettlementReportBlotterRow) => row.report,
    }),
    [getCustomContextMenuItems]
  );

  const blotterTable = useBlotterTable<LPSettlementReportBlotterRow>({
    dataObservable: lpSettlementReportsRowObservable,
    errorObservable,
    columns: defaultColumns,
    rowID: 'RowID' satisfies keyof LPSettlementReportBlotterRow,
    persistence: persisted,
    settings: blotterTableSettings,
    getExtraMainMenuItems,
    contextMenuOptions,
    gridOptions: {
      groupHideParentOfSingleChild: 'leafGroupsOnly',
      groupDefaultExpanded: -1,
      autoGroupColumnDef,
      rowGroupPanelShow: 'always',
      groupDisplayType: 'singleColumn',
      aggFuncs,
      onSelectionChanged: handleRowSelectionChanged,
      rowSelection,
      selectionColumnDef: { pinned: 'left', width: 32 },
      processCellForClipboard: formatExportCell,
    },
    exportOptions: {
      csvParams: {
        processCellCallback: params => cellCsvSafety(formatExportCell(params)),
      },
      excelParams: {
        processCellCallback: formatExportCell,
      },
      name: 'Unsettled Transactions',
    },
  });

  const { expandAllGroups, collapseAllGroups } = blotterTable;
  const extrasMenuPopover = useBlotterTableExtrasMenu();

  const handleSettleClick = useCallbackRef(() => {
    openManualBatchDialog(selectedTrades);
  });

  const handleAddToBatchClick = useCallbackRef(() => {
    openAddToBatchDialog(selectedTrades);
  });

  const handleManualBatchSubmit = useCallback(
    (createdBatchID: string) => {
      mixpanel.track(
        MixpanelEvent.LPSettlementBatchDetails,
        buildBatchDetailsOpenedEventProperties({
          openedFrom: LpSettlementBatchDetailsOpenedFrom.AfterCreate,
        })
      );
      openBatchDetails({ batchID: createdBatchID });
      manualBatchDialog.close();
    },
    [openBatchDetails, manualBatchDialog, mixpanel]
  );

  const handleAddToBatchSubmit = useCallback(
    ({ batchID: targetBatchID, transactionCount }: { batchID: string; transactionCount: number }) => {
      addToBatchDialog.close();
      addToast({
        text: (
          <VStack alignItems="flex-start" gap="spacing8" w="100%">
            <Text>
              {transactionCount === 1
                ? `1 transaction added to Batch ${targetBatchID}.`
                : `${transactionCount} transactions added to Batch ${targetBatchID}.`}
            </Text>
            <Button
              size={FormControlSizes.Small}
              data-testid="view-batch-toast-button"
              onClick={() => {
                mixpanel.track(
                  MixpanelEvent.LPSettlementBatchDetails,
                  buildBatchDetailsOpenedEventProperties({
                    openedFrom: LpSettlementBatchDetailsOpenedFrom.AfterAddToBatch,
                  })
                );
                openBatchDetails({ batchID: targetBatchID });
              }}
            >
              View Batch
            </Button>
          </VStack>
        ),
        variant: NotificationVariants.Positive,
        dismissable: true,
        timeout: 5000,
      });
    },
    [addToBatchDialog, addToast, mixpanel, openBatchDetails]
  );

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTable.blotterTableFiltersProps}
        portalId={portalID}
        quickFilterPrefix={
          <HStack gap="spacing8">
            {hasEditLPSettlemenyRole && <span>Total Transactions: {selectedTrades.length}</span>}
            {!readOnly && (
              <>
                <Tooltip tooltip={cannotSettleReason}>
                  <Button
                    size={FormControlSizes.Small}
                    disabled={!canSettle}
                    onClick={handleSettleClick}
                    startIcon={IconName.Plus}
                    data-testid="preview-settlement-button"
                    variant={ButtonVariants.Positive}
                  >
                    New Manual Batch
                  </Button>
                </Tooltip>
                <Tooltip tooltip={cannotSettleReason}>
                  <Button
                    size={FormControlSizes.Small}
                    disabled={!canSettle}
                    onClick={handleAddToBatchClick}
                    startIcon={IconName.Plus}
                    data-testid="add-to-batch-button"
                    variant={ButtonVariants.Primary}
                  >
                    Add to Batch
                  </Button>
                </Tooltip>
              </>
            )}
          </HStack>
        }
        suffix={
          <>
            <ExpandAllButton onClick={expandAllGroups} data-testid="expand-all-button" />
            <CollapseAllButton onClick={collapseAllGroups} />
            <BlotterTableExtrasMenu {...extrasMenuPopover}>
              {!showAllDecimalsInBlotters && (
                <Toggle
                  size={FormControlSizes.Small}
                  checked={showAllDecimals}
                  onChange={setShowAllDecimals}
                  label="Show All Decimals"
                  data-testid="show-all-decimals-toggle"
                />
              )}
              <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />
            </BlotterTableExtrasMenu>
          </>
        }
      />
      <BlotterWrapper data-testid="unsettled-transactions-blotter">
        <BlotterTable {...blotterTable} />
      </BlotterWrapper>
      <NewManualBatchDialog onSubmit={handleManualBatchSubmit} {...manualBatchDialog} />
      <AddToBatchDialog onSubmit={handleAddToBatchSubmit} {...addToBatchDialog} />
    </>
  );
};

function UnsettledTransactionsBlotterWithFilters(props: UnsettledTransactionsBlotterParams) {
  const persisted = usePersistedBlotterTable(props.blotterID, { filter: defaultUnsettledTransactionsFilter });
  const { filter, filterBuilderProps, filterableProperties } = useUnsettledTransactionsFilter({
    persistedBlotterTable: persisted,
  });
  const filterBuilderAccordion = useAccordionFilterBuilder({ filterBuilderProps });

  return (
    <UnsettledTransactionsBlotterContent
      {...props}
      persisted={persisted}
      symbols={filter.Symbols}
      filterBuilderAccordion={filterBuilderAccordion}
      filterableProperties={filterableProperties}
    />
  );
}

function UnsettledTransactionsBlotterReadOnly(props: UnsettledTransactionsBlotterParams & { batchID: string }) {
  const persisted = usePersistedBlotterTable(props.blotterID);

  return <UnsettledTransactionsBlotterContent {...props} persisted={persisted} />;
}

export function UnsettledTransactionsBlotter(props: UnsettledTransactionsBlotterParams) {
  if (props.batchID != null) {
    return <UnsettledTransactionsBlotterReadOnly {...props} batchID={props.batchID} />;
  }

  return <UnsettledTransactionsBlotterWithFilters {...props} />;
}
